import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { httpClient } from '@/lib/http-client';
import { getEndpointUrl } from '@/lib/api-config';
import type { PacDetectedAccount } from './PacAccountRow';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: PacDetectedAccount;
  customerCode: string | null;
  onReceiptUploaded: () => void;
}

const PAC_SERVICE_NAME = 'Junngla';

export default function PacActivationDialog({ open, onOpenChange, account, customerCode, onReceiptUploaded }: Props) {
  const [bankOpened, setBankOpened] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  const openBank = () => {
    if (!account.pacBankUrl) return;
    window.open(account.pacBankUrl, '_blank', 'noopener,noreferrer');
    setBankOpened(true);
  };

  const copyCode = async () => {
    if (!customerCode) return;
    try {
      await navigator.clipboard.writeText(customerCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  const handleFileSelect = async (file: File) => {
    if (!account.bankCode) return;
    setUploading(true);
    setUploadError(null);
    try {
      const signedRes = await httpClient.post(getEndpointUrl('pac', 'receipt-upload-url'), {
        contentType: file.type || 'application/octet-stream',
      });
      const signedData = (await signedRes.json()) as { uploadUuid?: string; endpoint?: string };
      if (!signedData.uploadUuid || !signedData.endpoint) throw new Error('Respuesta inválida del servidor');

      const uploadRes = await fetch(signedData.endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!uploadRes.ok) throw new Error(`Error ${uploadRes.status} subiendo archivo a S3`);

      const registerRes = await httpClient.post(getEndpointUrl('pac', 'receipt-register'), {
        bankCode: account.bankCode,
        accountNumber: account.numeroCuenta,
        uploadUuid: signedData.uploadUuid,
      });
      await registerRes.json();

      setUploadedFileName(file.name);
      onReceiptUploaded();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Error subiendo el comprobante');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Activar mandato PAC</DialogTitle>
          <DialogDescription>
            <span className="block">
              {account.banco} · <span className="font-mono">{account.numeroCuenta}</span>
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <ol className="space-y-3">
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-flux-primary/10 text-xs font-semibold text-flux-primary">1</span>
              <span>
                <strong>Abre el portal de tu banco</strong> haciendo clic en el botón de abajo. Inicia sesión con tus credenciales habituales.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-flux-primary/10 text-xs font-semibold text-flux-primary">2</span>
              <span>
                Busca la <strong>sección de mandatos PAC</strong> (Pago Automático de Cuentas). Suele estar en el menú de pagos o servicios.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-flux-primary/10 text-xs font-semibold text-flux-primary">3</span>
              <span>
                Crea un mandato nuevo y selecciona <strong>"{PAC_SERVICE_NAME}"</strong> en el listado de empresas.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-flux-primary/10 text-xs font-semibold text-flux-primary">4</span>
              <span>
                <strong>Pega tu código de cliente</strong> (mostrado abajo) y déjalo <strong>sin monto máximo</strong> para que cobremos el valor exacto de cada cuota.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-flux-primary/10 text-xs font-semibold text-flux-primary">5</span>
              <span>
                Confirma el mandato con tu método de autenticación (clave dinámica, Digipass, biometría, etc.).
              </span>
            </li>
          </ol>

          <div className="rounded-lg border bg-card p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tu código de cliente</p>
            <div className="mt-2 flex items-center justify-between gap-3">
              {customerCode ? (
                <code className="font-mono text-2xl font-semibold tracking-wider text-flux-primary">{customerCode}</code>
              ) : (
                <span className="font-mono text-2xl text-muted-foreground">—</span>
              )}
              <button
                type="button"
                onClick={() => void copyCode()}
                disabled={!customerCode}
                className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-3 py-1.5 text-xs font-medium hover:bg-slate-200 disabled:opacity-50 dark:bg-slate-800 dark:hover:bg-slate-700"
              >
                <span className="material-symbols-outlined text-base">{codeCopied ? 'check' : 'content_copy'}</span>
                {codeCopied ? 'Copiado' : 'Copiar'}
              </button>
            </div>
          </div>

          {!bankOpened ? (
            <button
              type="button"
              onClick={openBank}
              disabled={!account.pacBankUrl}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-flux-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-flux-primary/90 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-base">open_in_new</span>
              Abrir portal del banco
            </button>
          ) : uploadedFileName ? (
            <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-2.5 text-sm dark:border-green-900 dark:bg-green-900/20">
              <span className="material-symbols-outlined text-base text-green-600 dark:text-green-400">check_circle</span>
              <span className="text-green-800 dark:text-green-300">Comprobante subido: {uploadedFileName}</span>
            </div>
          ) : (
            <div>
              <label className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-md bg-flux-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-flux-primary/90">
                <span className="material-symbols-outlined text-base">upload_file</span>
                {uploading ? 'Subiendo…' : 'Subir comprobante (opcional)'}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleFileSelect(file);
                    e.target.value = '';
                  }}
                />
              </label>
              <p className="mt-2 text-xs text-muted-foreground">
                Si tu banco te entregó un comprobante de la activación, puedes subirlo para que validemos tu mandato más rápido. <strong>No es obligatorio.</strong>
              </p>
              {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
            </div>
          )}

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 dark:border-blue-900 dark:bg-blue-900/20 dark:text-blue-200">
            <div className="flex gap-2">
              <span className="material-symbols-outlined text-base text-blue-600 dark:text-blue-300">schedule</span>
              <p>
                Una vez que confirmes el mandato en tu banco, tomará <strong>algunos días en reflejarse en {PAC_SERVICE_NAME}</strong> (depende del banco). No es necesario que esperes acá: puedes
                cerrar la ventana cuando termines.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <button type="button" onClick={() => onOpenChange(false)} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800">
            Cerrar
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
